import { useCallback, useMemo } from 'react'
import * as Yup from 'yup'
import { MessageParams } from 'yup'
import { InputProps } from '@fluentui/react-input'
import { CheckboxProps } from '@fluentui/react-checkbox'
import { BaseComboboxProps } from '../components/Combobox/BaseCombobox'
import { LocalDatePickerProps } from '../components/DatePicker/LocalDatePicker'
import { MoneyInputProps } from '../components/Input/MoneyInput'
import { TextareaProps } from '@fluentui/react-components'
import useGlobalContext from './useGlobalContext'
import { AttributesListRowProps, getAttributeValue, setAttributeValue } from '../components/Form/Attributes/AttributeDefinitionRow'
import Sandbox from '@nyariv/sandboxjs'

export enum AttributeType {
    Boolean = 'BOOLEAN',
    KeyValueList = 'KEYVALUE_LIST',
    LocalDate = 'LOCALDATE',
    Money = 'MONEY',
    Number = 'NUMBER',
    String = 'STRING',
    Text = 'TEXT',
    User = 'USER',
    File = 'FILE',
}

export enum EntityAttributeType {
    File = 'FILE',
    Invoice = 'INVOICE',
    Party = 'PARTY',
}

export enum AttributeContextType {
    File = 'FILE',
    NaturalPerson = 'NATURAL_PERSON',
    LegalPerson = 'LEGAL_PERSON',
    Invoice = 'INVOICE',
}

export enum AttributeViewType {
    Input = 'INPUT',
    View = 'VIEW',
    Filter = 'FILTER',
}

export type AttributeDefinitionProperties = Partial<InputProps> &
    Partial<CheckboxProps> &
    Partial<BaseComboboxProps<any>> &
    Partial<LocalDatePickerProps> &
    Partial<MoneyInputProps> &
    Partial<TextareaProps> & {
        //generic
        initialValue?: any // Use this value when creating a new file/party/invoice
        warning?: boolean // Does this attribute serve as a static warning field?
        //datepicker
        useToday?: boolean // Use today as initialValue
    }

export type AttributeDependencyDto = {
    id: string
    value?: any[]
    evaluation?: string
}

export type AttributeDefinitionDto = {
    id: string
    name: string
    dataType: AttributeType
    context: AttributeContextType[]
    category?: string
    position?: number
    enabled: boolean
    description?: string
    properties?: AttributeDefinitionProperties
    filter: boolean
    dependencies?: AttributeDependencyDto[]
}

export type AttributeDto = {
    id?: string
    definitionId: string
    entityId?: string
    entityType: EntityAttributeType
    value: any
}

export type AttributeEntityDto = {
    attributes?: AttributeDto[]
    currency?: string
}

export type AttributesReturnProps = {
    loaded: boolean
    attributeDefinitions: AttributeDefinitionDto[]
    activeAttributeDefinitions: AttributeDefinitionDto[]
    attributeCategories: string[]
    applyDefaultValues: (values: any) => void
}

export const isAttributeValueNonNull = (value: any): boolean => {
    return value !== undefined && value !== 'mixed' && value !== '' && value !== '0.00' && !(Array.isArray(value) && (value as []).length === 0)
}

export const evaluateDependencyValue = (foundDep: AttributeDto, evaluation: string): boolean => {
    const scope = { value: foundDep.value }
    const sandbox = new Sandbox()
    const exec = sandbox.compile(evaluation)
    const result = exec(scope).run()
    return result as boolean
}

export function findAttributeWithValue(attributes: AttributeDto[], definition: AttributeDefinitionDto): AttributeDto[] {
    return attributes.filter(value => value.definitionId === definition.id).filter(value => isAttributeValueNonNull(value.value))
}

export function getAttributeValidationSchema(translate: (key: string, values?: any) => string, attributeDefinitions: AttributeDefinitionDto[]) {
    return Yup.array()
        .nullable()
        .label(translate('file.edit.form.finance.attributes'))
        .test({
            message: (props: MessageParams) => {
                return translate('base.form.attributes.required', {
                    names: attributeDefinitions
                        .filter(def => def.properties?.required === true)
                        .filter(def => findAttributeWithValue((props.value || []) as AttributeDto[], def)?.length === 0)
                        .map(def => def.name)
                        .join(', '),
                    interpolation: { escapeValue: false },
                })
            },
            test: (values: AttributeDto[]) => {
                return (
                    attributeDefinitions
                        .filter(def => def.properties?.required === true)
                        .filter(def => findAttributeWithValue((values || []) as AttributeDto[], def)?.length === 0).length === 0
                )
            },
        })
}

export default function useAttributes(
    filterViewType?: AttributeViewType,
    filterContext?: AttributeContextType[],
    filterCategory?: string,
    entityType?: EntityAttributeType,
): AttributesReturnProps {
    const { loaded, entities } = useGlobalContext('attributeDefinitions')

    const attributeDefinitions = useMemo<AttributeDefinitionDto[]>(() => {
        return (
            entities
                //filter definition based on category name.
                ?.filter(definition => definition.category === filterCategory || filterCategory === undefined)
                //filter definition based on view type.
                .filter(definition => filterViewType !== AttributeViewType.Filter || definition.filter)
                //filter definition on context.
                .filter(definition => !definition.context || definition.context?.some(context => filterContext?.includes(context)))
                //sort based on position
                .sort((a, b) => (a.position || 1000) - (b.position || 1000)) || []
        )
    }, [entities])

    const activeAttributeDefinitions = useMemo<AttributeDefinitionDto[]>(() => {
        return attributeDefinitions.filter(definition => definition.enabled)
    }, [attributeDefinitions])

    const attributeCategories = useMemo<string[]>(() => {
        return activeAttributeDefinitions.map(definition => definition.category).filter((category, index, array) => array.indexOf(category) === index)
    }, [attributeDefinitions])

    const applyDefaultValues = useCallback(
        (initialValues: any) => {
            const defaultValueAttributes = attributeDefinitions
                ?.filter(definition => definition.enabled)
                ?.filter(definition => !definition.dependencies)
            if (defaultValueAttributes && defaultValueAttributes.length > 0) {
                defaultValueAttributes.forEach(definition => {
                    const props: AttributesListRowProps = {
                        context: filterContext,
                        definition: definition,
                        entity: initialValues,
                        entityType: entityType,
                        orientation: 'horizontal',
                        viewType: filterViewType,
                    }
                    const value = getAttributeValue(props)(undefined)
                    setAttributeValue(props)(value)
                })
            }
        },
        [attributeDefinitions],
    )

    return { loaded, attributeDefinitions, activeAttributeDefinitions, attributeCategories, applyDefaultValues }
}
